import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Palette } from "lucide-react";

interface ThemeConfig {
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  warning_color: string;
  error_color: string;
  border_radius: string;
  font_scale: string;
}

interface ThemeConfigurationCardProps {
  title: string;
  description: string;
  themeConfig: ThemeConfig;
  onThemeChange: (config: ThemeConfig) => void;
  onSave: () => Promise<void>;
  loading?: boolean;
  saving?: boolean;
  isSystemDefault?: boolean;
}

export const ThemeConfigurationCard = ({
  title,
  description,
  themeConfig,
  onThemeChange,
  onSave,
  loading = false,
  saving = false,
  isSystemDefault = false
}: ThemeConfigurationCardProps) => {
  const handleColorChange = (colorKey: keyof ThemeConfig, value: string) => {
    onThemeChange({
      ...themeConfig,
      [colorKey]: value
    });
  };

  const handleLayoutChange = (layoutKey: keyof ThemeConfig, value: string) => {
    onThemeChange({
      ...themeConfig,
      [layoutKey]: value
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const colorFields = [
    { key: 'primary_color' as const, label: 'Primary Color', description: 'Main brand color' },
    { key: 'secondary_color' as const, label: 'Secondary Color', description: 'Supporting brand color' },
    { key: 'accent_color' as const, label: 'Accent Color', description: 'Success and positive actions' },
    { key: 'warning_color' as const, label: 'Warning Color', description: 'Caution and alerts' },
    { key: 'error_color' as const, label: 'Error Color', description: 'Errors and destructive actions' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          {description}
          {isSystemDefault && (
            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded text-sm">
              <strong>System Default:</strong> These settings serve as fallback values for organizations without custom themes.
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="brand" className="w-full">
          <div className="border-b border-border">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="brand">Brand Colors</TabsTrigger>
              <TabsTrigger value="layout">Layout</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="brand" className="space-y-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {colorFields.map(({ key, label, description }) => (
                <div key={key} className="space-y-2">
                  <Label>{label}</Label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                  <div className="flex items-center space-x-2">
                    <Input 
                      type="color" 
                      value={themeConfig[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-20 h-10"
                      disabled={saving}
                    />
                    <Input 
                      value={themeConfig[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="flex-1 font-mono text-sm"
                      disabled={saving}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="layout" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Border Radius</Label>
                <p className="text-xs text-muted-foreground">Controls roundness of UI elements</p>
                <Select 
                  value={themeConfig.border_radius}
                  onValueChange={(value) => handleLayoutChange('border_radius', value)}
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Square)</SelectItem>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                    <SelectItem value="full">Full (Rounded)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Font Scale</Label>
                <p className="text-xs text-muted-foreground">Adjusts text size and spacing</p>
                <Select 
                  value={themeConfig.font_scale}
                  onValueChange={(value) => handleLayoutChange('font_scale', value)}
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="comfortable">Comfortable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Brand Color Preview */}
        <div className="space-y-4 p-4 bg-muted/10 rounded-lg border">
          <h4 className="text-sm font-medium">Brand Color Preview</h4>
          <div className="flex flex-wrap items-center gap-3">
            {colorFields.map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig[key] }}
                />
                <span className="text-sm">{label.replace(' Color', '')}</span>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={onSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Theme Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};